import { useEffect, useState, useRef } from "react";
import {
  NovuProvider,
  useNotifications,
  useCounts,
  useNovu,
} from "@novu/react";
import type { Notification } from "@novu/react";
import axios from "axios";
import styled from "styled-components";
import { useHistory } from "react-router-dom";
import { useRecoilState } from "recoil";
import { Flex } from "rebass/styled-components";
import { BiSolidEnvelope } from "react-icons/bi";
import { GoDotFill } from "react-icons/go";

import { useUser } from "../../Hooks/useUser";
import { AllstarNovu } from "../../State";
import {
  requestFCMPermission,
  isFirebaseConfigValid,
} from "MVVM/Utilities/Firebase/firebase";
import { setupNotificationAnalyticsListener } from "MVVM/Utilities/Firebase/notificationAnalytics";
import { analytics } from "MVVM/Analytics";

export const NovuComponent = () => {
  const { allstarUser } = useUser();
  const [allstarNovuParams, setAllstarNovuParams] = useRecoilState(AllstarNovu);

  useEffect(() => {
    setupNotificationAnalyticsListener();
  }, []);

  useEffect(() => {
    async function getNovuSubscriber(token: string) {
      const notificationsUrl = process.env.REACT_APP_AWS_SERVERLESS
        ? `${process.env.REACT_APP_AWS_SERVERLESS}/notifications`
        : "https://gejz7gcvbi.execute-api.us-east-1.amazonaws.com/default/dev-allstar-novu-subscribe";

      try {
        const response = await axios.get(notificationsUrl, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (!response.data) return;

        const { subscriberId, subscriberHash, appId } = response.data;
        if (!subscriberId || !subscriberHash || !appId) return;

        setAllstarNovuParams({
          ...allstarNovuParams,
          valid: true,
          subscriberId,
          subscriberHash,
          appId,
        });
      } catch (e) {}
    }
    if (!allstarNovuParams.valid) getNovuSubscriber(allstarUser?.token);
  }, [allstarUser, allstarNovuParams, setAllstarNovuParams]);

  useEffect(() => {
    async function setupFCM() {
      if (!isFirebaseConfigValid()) return;
      if (!allstarNovuParams.valid || !allstarUser?.loggedIn) return;
      if (allstarNovuParams.fcmToken) return;

      try {
        const fcmToken = await requestFCMPermission();
        if (!fcmToken) return;

        setAllstarNovuParams({
          ...allstarNovuParams,
          fcmToken,
        });

        const notificationsUrl = process.env.REACT_APP_AWS_SERVERLESS
          ? `${process.env.REACT_APP_AWS_SERVERLESS}/notifications`
          : "https://gejz7gcvbi.execute-api.us-east-1.amazonaws.com/default/dev-allstar-novu-subscribe";

        await axios.post(
          notificationsUrl,
          {
            type: "credentials",
            provider: "FCM",
            deviceToken: fcmToken,
            subscriberId: allstarNovuParams.subscriberId,
          },
          {
            headers: {
              Authorization: `Bearer ${allstarUser?.token}`,
            },
          },
        );
      } catch (e) {}
    }

    setupFCM();
  }, [
    allstarNovuParams,
    allstarUser?.loggedIn,
    allstarUser?.token,
    setAllstarNovuParams,
  ]);

  if (!allstarUser.loggedIn || !allstarNovuParams.valid) return <></>;

  return (
    <NovuProvider
      applicationIdentifier={allstarNovuParams.appId}
      subscriberId={allstarNovuParams.subscriberId}
      subscriberHash={allstarNovuParams.subscriberHash}
    >
      <NotificationBell />
    </NovuProvider>
  );
};

const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const novu = useNovu();
  const { notifications, isLoading, readAll, fetchMore, hasMore, refetch } =
    useNotifications();
  const { counts } = useCounts({ filters: [{ read: false }] });
  const history = useHistory();

  useEffect(() => {
    const cleanup = novu.on(
      "notifications.notification_received" as any,
      () => {
        refetch();
      },
    );
    return cleanup;
  }, [novu, refetch]);

  const unreadCount = counts?.[0]?.count ?? 0;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        bellRef.current &&
        !bellRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function getNotificationUrl(notification: Notification): string | null {
    const templateIdentifier = notification.data?.templateIdentifier as
      | string
      | undefined;

    // v0 legacy: data-based routing
    if (notification.data?.shareId) {
      const shareId = notification.data.shareId;
      if (templateIdentifier === "new-comment")
        return `/clip/${shareId}?tab=comments`;

      const isMobile =
        notification.data.isMobile === "true" ||
        notification.data.isMobile !== "false";
      return `/clip/${shareId}${isMobile ? "?mobileView=true" : ""}`;
    }

    const action = notification.data?.action as
      | Record<string, string>
      | undefined;
    if (action?.active) {
      if (action.external) return action.long;
      return action.short;
    }

    if (templateIdentifier === "match-ready") return "/match-history";

    if (templateIdentifier === "new-follow" && notification.data?._id)
      return `/u/${notification.data._id}`;

    // v2: top-level redirect
    if (notification.redirect?.url) return notification.redirect.url;

    // v2: primaryAction redirect (fallback)
    if (notification.primaryAction?.redirect?.url)
      return notification.primaryAction.redirect.url;

    return null;
  }

  function handleNotificationClick(notification: Notification) {
    const templateIdentifier = notification.data?.templateIdentifier as
      | string
      | undefined;

    analytics.track("Button - Click", {
      location: "notifications",
      locationIdentifier: templateIdentifier,
      source: "website",
    });

    setOpen(false);

    const url = getNotificationUrl(notification);
    if (!url) return;

    const action = notification.data?.action as
      | Record<string, string>
      | undefined;
    if (action?.active && action.external) {
      window.open(url);
    } else {
      history.push(url);
    }
  }

  function handlePrimaryAction(notification: Notification) {
    const templateIdentifier = notification.data?.templateIdentifier as
      | string
      | undefined;

    analytics.track("Button - Click", {
      location: "notifications",
      locationIdentifier: templateIdentifier,
      source: "website",
    });

    setOpen(false);

    const url = getNotificationUrl(notification);
    if (url) history.push(url);
  }

  function getTimeAgo(date: string) {
    const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
    if (seconds < 60) return "just now";
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    return `${months}mo ago`;
  }

  return (
    <Wrapper>
      <BellButton ref={bellRef} onClick={() => setOpen(!open)}>
        <Flex style={{ position: "relative" }}>
          <StyledGoDotFill showBubble={unreadCount > 0} />
          <StyledBiSolidEnvelope />
        </Flex>
      </BellButton>

      {open && (
        <Dropdown ref={dropdownRef}>
          <DropdownHeader>
            <HeaderTitle>Notifications</HeaderTitle>
            {unreadCount > 0 && (
              <MarkReadButton onClick={() => readAll()}>
                Mark all as read
              </MarkReadButton>
            )}
          </DropdownHeader>

          <NotificationsList>
            {isLoading && <EmptyState>Loading...</EmptyState>}

            {!isLoading && (!notifications || notifications.length === 0) && (
              <EmptyState>No notifications</EmptyState>
            )}

            {notifications?.map((notification) => (
              <NotificationItem
                key={notification.id}
                isRead={notification.isRead}
                onClick={() => handleNotificationClick(notification)}
              >
                {!notification.isRead && <UnreadDot />}
                <NotificationContent>
                  <NotificationBody>{notification.body}</NotificationBody>
                  <NotificationMeta>
                    <TimeAgo>{getTimeAgo(notification.createdAt)}</TimeAgo>
                  </NotificationMeta>
                  {notification.primaryAction && (
                    <PrimaryActionButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePrimaryAction(notification);
                      }}
                    >
                      {notification.primaryAction.label}
                    </PrimaryActionButton>
                  )}
                </NotificationContent>
              </NotificationItem>
            ))}

            {hasMore && (
              <LoadMoreButton onClick={() => fetchMore()}>
                Load more
              </LoadMoreButton>
            )}
          </NotificationsList>
        </Dropdown>
      )}
    </Wrapper>
  );
};

/* ── Styled Components ── */

const Wrapper = styled.div`
  position: relative;
`;

const BellButton = styled.div`
  cursor: pointer;
`;

const StyledBiSolidEnvelope = styled(BiSolidEnvelope)`
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  height: 24px;
  width: 24px;
`;

const StyledGoDotFill = styled(GoDotFill)<{ showBubble: boolean }>`
  color: ${({ theme }) => theme.colors.alert};
  cursor: pointer;
  position: absolute;
  right: -6px;
  top: -3px;
  z-index: 4;
  ${(props) => (props.showBubble ? "display: block" : "display: none")}
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 400px;
  max-height: 520px;
  background-color: #131d25;
  border: 1px solid rgba(143, 169, 175, 0.1);
  border-radius: 16px;
  box-shadow:
    2px 2px 10px rgba(12, 19, 24, 0.6),
    0 20px 50px rgba(0, 0, 0, 0.4);
  z-index: 300;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const DropdownHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 20px 16px;
  border-bottom: 1px solid rgba(143, 169, 175, 0.08);
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-family: "Barlow", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #f7fcfd;
`;

const MarkReadButton = styled.button`
  background: none;
  border: none;
  color: #8fa9af;
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 8px;
  transition: color 0.2s ease;

  &:hover {
    color: #4dbd94;
  }
`;

const NotificationsList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
  scrollbar-color: #253a49 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: #253a49;
    border-radius: 999px;
  }
`;

const NotificationItem = styled.div<{ isRead: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 20px;
  cursor: pointer;
  position: relative;
  transition: background-color 0.15s ease;
  background-color: ${({ isRead }) =>
    isRead ? "transparent" : "rgba(77, 189, 148, 0.04)"};

  &:hover {
    background-color: #1a2933;
  }
`;

const UnreadDot = styled.div`
  width: 8px;
  height: 8px;
  min-width: 8px;
  border-radius: 50%;
  background-color: #4dbd94;
  margin-top: 6px;
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationBody = styled.p`
  margin: 0;
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #f7fcfd;
  line-height: 1.4;
`;

const NotificationMeta = styled.div`
  display: flex;
  align-items: center;
  margin-top: 6px;
`;

const TimeAgo = styled.span`
  font-family: "Barlow", sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: #8fa9af;
`;

const PrimaryActionButton = styled.button`
  margin-top: 10px;
  background-color: #4dbd94;
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 6px 20px;
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #45aa85;
  }
`;

const LoadMoreButton = styled.button`
  display: block;
  width: 100%;
  background: none;
  border: none;
  border-top: 1px solid rgba(143, 169, 175, 0.08);
  color: #8fa9af;
  font-family: "Barlow", sans-serif;
  font-size: 13px;
  font-weight: 500;
  padding: 14px;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: #4dbd94;
  }
`;

const EmptyState = styled.div`
  padding: 40px 20px;
  text-align: center;
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  color: #8fa9af;
`;
